import { z } from "zod";
// Exchange (SSP <-> DSP) contract: endpoint status / pricing / routing filters,
// the per-minute counter metric ids, and exact money arithmetic helpers. ONE
// source of truth shared by the backend (serving + reporting) and the frontend
// (endpoint forms + report formulas).
// --- Endpoint status / pricing ---------------------------------------------
export const EndpointStatusSchema = z.enum(["active", "paused"]);
// profit_share: SSP is paid its floor plus half the profit above floor + our
//   commission (the DSP endpoint's margin% of the floor). The RTB default.
// revshare: SSP is paid a % of the DSP clearing price (RTB).
// fixed_cpm: SSP is paid a fixed buy CPM per impression (VAST).
export const PricingModelSchema = z.enum([
    "profit_share",
    "revshare",
    "fixed_cpm",
]);
// --- Routing filters --------------------------------------------------------
//
// Every attribute filter is All / Include / Exclude over a list of values.
// `all` (or an absent attribute) never restricts. Values are strings on the
// wire; the evaluator normalizes per attribute (domains are lower-cased and
// suffix-matched, device types are OpenRTB ints as strings, etc).
export const FilterModeSchema = z.enum(["all", "include", "exclude"]);
export const AttributeFilterSchema = z.object({
    mode: FilterModeSchema,
    values: z.array(z.string().min(1).max(256)).max(5000),
});
export const ENDPOINT_FILTER_KEYS = [
    "country",
    "trafficType",
    "deviceType",
    "os",
    "domain",
    "bundle",
    "adFormat",
    "segment",
    "dealId",
];
export const EndpointFiltersSchema = z
    .object({
    country: AttributeFilterSchema.optional(),
    trafficType: AttributeFilterSchema.optional(),
    deviceType: AttributeFilterSchema.optional(),
    os: AttributeFilterSchema.optional(),
    domain: AttributeFilterSchema.optional(),
    bundle: AttributeFilterSchema.optional(),
    adFormat: AttributeFilterSchema.optional(),
    segment: AttributeFilterSchema.optional(),
    dealId: AttributeFilterSchema.optional(),
})
    .strict();
// Traffic type derived from the OpenRTB request (site vs app + devicetype).
// Stored as a smallint on adRequest.trafficType.
export var TrafficType;
(function (TrafficType) {
    TrafficType[TrafficType["Unknown"] = 0] = "Unknown";
    TrafficType[TrafficType["Web"] = 1] = "Web";
    TrafficType[TrafficType["MobileWeb"] = 2] = "MobileWeb";
    TrafficType[TrafficType["App"] = 3] = "App";
    TrafficType[TrafficType["Ctv"] = 4] = "Ctv";
    TrafficType[TrafficType["Ott"] = 5] = "Ott";
})(TrafficType || (TrafficType = {}));
export const TRAFFIC_TYPE_LABELS = {
    [TrafficType.Unknown]: "Unknown",
    [TrafficType.Web]: "Web",
    [TrafficType.MobileWeb]: "Mobile Web",
    [TrafficType.App]: "App",
    [TrafficType.Ctv]: "CTV",
    [TrafficType.Ott]: "OTT",
};
// Ad format of the (first) impression. Stored as a smallint on adRequest.adFormat.
export var AdFormat;
(function (AdFormat) {
    AdFormat[AdFormat["Unknown"] = 0] = "Unknown";
    AdFormat[AdFormat["Video"] = 1] = "Video";
    AdFormat[AdFormat["Banner"] = 2] = "Banner";
    AdFormat[AdFormat["Native"] = 3] = "Native";
    AdFormat[AdFormat["Audio"] = 4] = "Audio";
    AdFormat[AdFormat["Pop"] = 5] = "Pop";
})(AdFormat || (AdFormat = {}));
export const AD_FORMAT_LABELS = {
    [AdFormat.Unknown]: "Unknown",
    [AdFormat.Video]: "Video",
    [AdFormat.Banner]: "Banner",
    [AdFormat.Native]: "Native",
    [AdFormat.Audio]: "Audio",
    [AdFormat.Pop]: "Pop",
};
// --- Exchange counters ------------------------------------------------------
//
// Per-minute counters written by the serving pool (`exchangeCounter` table).
// Values are STABLE ids — append only. The reporting engine and the public
// statistics link both read these; nothing else derives these numbers.
export var ExchangeMetric;
(function (ExchangeMetric) {
    // SSP (inbound) side — keyed by providerUuid.
    ExchangeMetric[ExchangeMetric["SspRequests"] = 1] = "SspRequests";
    ExchangeMetric[ExchangeMetric["SspThrottled"] = 2] = "SspThrottled";
    ExchangeMetric[ExchangeMetric["SspPaused"] = 3] = "SspPaused";
    ExchangeMetric[ExchangeMetric["SspFiltered"] = 4] = "SspFiltered";
    ExchangeMetric[ExchangeMetric["SspWins"] = 5] = "SspWins";
    // DSP (outbound) side — keyed by providerUuid + campaignUuid.
    ExchangeMetric[ExchangeMetric["DspRequests"] = 10] = "DspRequests";
    ExchangeMetric[ExchangeMetric["DspSkippedQps"] = 11] = "DspSkippedQps";
    ExchangeMetric[ExchangeMetric["DspSkippedFilter"] = 12] = "DspSkippedFilter";
    ExchangeMetric[ExchangeMetric["DspResponses"] = 13] = "DspResponses";
    ExchangeMetric[ExchangeMetric["DspNoBid"] = 14] = "DspNoBid";
    ExchangeMetric[ExchangeMetric["DspTimeouts"] = 15] = "DspTimeouts";
    ExchangeMetric[ExchangeMetric["DspHttpErrors"] = 16] = "DspHttpErrors";
    ExchangeMetric[ExchangeMetric["InvalidBids"] = 17] = "InvalidBids";
    ExchangeMetric[ExchangeMetric["BidsBelowFloor"] = 18] = "BidsBelowFloor";
    ExchangeMetric[ExchangeMetric["DspWins"] = 19] = "DspWins";
    ExchangeMetric[ExchangeMetric["BelowFloorAfterShare"] = 20] = "BelowFloorAfterShare";
    ExchangeMetric[ExchangeMetric["ValidBids"] = 21] = "ValidBids";
    // Segments — keyed by segmentId.
    ExchangeMetric[ExchangeMetric["SegmentMatched"] = 30] = "SegmentMatched";
    // Infra.
    ExchangeMetric[ExchangeMetric["RedisUnavailable"] = 40] = "RedisUnavailable";
    // Popunder lane — keyed by providerUuid (+ campaignUuid when known). One
    // primary counter per outcome; see reports/Popunder RTB full plan.md §5.1.
    ExchangeMetric[ExchangeMetric["PopRequests"] = 50] = "PopRequests";
    ExchangeMetric[ExchangeMetric["PopDisabled"] = 51] = "PopDisabled";
    ExchangeMetric[ExchangeMetric["PopNoMatch"] = 52] = "PopNoMatch";
    ExchangeMetric[ExchangeMetric["PopExhausted"] = 53] = "PopExhausted";
    ExchangeMetric[ExchangeMetric["PopBelowFloor"] = 54] = "PopBelowFloor";
    ExchangeMetric[ExchangeMetric["PopIvt"] = 55] = "PopIvt";
    ExchangeMetric[ExchangeMetric["PopNoRedis"] = 56] = "PopNoRedis";
    ExchangeMetric[ExchangeMetric["PopDeadline"] = 57] = "PopDeadline";
    ExchangeMetric[ExchangeMetric["PopCappedAtBid"] = 58] = "PopCappedAtBid";
    ExchangeMetric[ExchangeMetric["PopError"] = 59] = "PopError";
    ExchangeMetric[ExchangeMetric["PopBids"] = 60] = "PopBids";
    ExchangeMetric[ExchangeMetric["PopWins"] = 61] = "PopWins";
    ExchangeMetric[ExchangeMetric["PopBilled"] = 62] = "PopBilled";
    ExchangeMetric[ExchangeMetric["PopNoticeInvalid"] = 63] = "PopNoticeInvalid";
    ExchangeMetric[ExchangeMetric["PopVisits"] = 64] = "PopVisits";
    ExchangeMetric[ExchangeMetric["PopCapUnchecked"] = 65] = "PopCapUnchecked";
    ExchangeMetric[ExchangeMetric["PopCappedAtVisit"] = 66] = "PopCappedAtVisit";
    ExchangeMetric[ExchangeMetric["PopInvalidToken"] = 67] = "PopInvalidToken";
    ExchangeMetric[ExchangeMetric["PopReplay"] = 68] = "PopReplay";
    ExchangeMetric[ExchangeMetric["PopVisitPersistFailed"] = 69] = "PopVisitPersistFailed";
    ExchangeMetric[ExchangeMetric["PopConversions"] = 70] = "PopConversions";
    ExchangeMetric[ExchangeMetric["PopPostbackRejected"] = 71] = "PopPostbackRejected";
    ExchangeMetric[ExchangeMetric["PopTokenTooLarge"] = 72] = "PopTokenTooLarge";
    ExchangeMetric[ExchangeMetric["PopVisitUncertain"] = 73] = "PopVisitUncertain";
    ExchangeMetric[ExchangeMetric["PopReservationReleased"] = 74] = "PopReservationReleased";
    // 75 unused (was the cap sweeper, never shipped).
    ExchangeMetric[ExchangeMetric["PopLatGt25"] = 76] = "PopLatGt25";
    ExchangeMetric[ExchangeMetric["PopHead"] = 77] = "PopHead";
    ExchangeMetric[ExchangeMetric["PopPrefetch"] = 78] = "PopPrefetch";
    ExchangeMetric[ExchangeMetric["PopExpired"] = 79] = "PopExpired";
    ExchangeMetric[ExchangeMetric["PopExpiredStale"] = 80] = "PopExpiredStale";
    ExchangeMetric[ExchangeMetric["PopCappedReplay"] = 81] = "PopCappedReplay";
    ExchangeMetric[ExchangeMetric["PopWrongPurpose"] = 82] = "PopWrongPurpose";
    ExchangeMetric[ExchangeMetric["PopNoticeIpDenied"] = 83] = "PopNoticeIpDenied";
    ExchangeMetric[ExchangeMetric["PopNoticePersistFailed"] = 84] = "PopNoticePersistFailed";
    ExchangeMetric[ExchangeMetric["PopNoticeUncertain"] = 85] = "PopNoticeUncertain";
    ExchangeMetric[ExchangeMetric["PopVisitIvt"] = 86] = "PopVisitIvt";
    ExchangeMetric[ExchangeMetric["PopFpMismatch"] = 87] = "PopFpMismatch";
    ExchangeMetric[ExchangeMetric["PopConfirmed"] = 88] = "PopConfirmed";
    ExchangeMetric[ExchangeMetric["PopPostbackStatus"] = 89] = "PopPostbackStatus";
    ExchangeMetric[ExchangeMetric["PopRegionUnknown"] = 90] = "PopRegionUnknown";
    ExchangeMetric[ExchangeMetric["PopPriceMismatch"] = 91] = "PopPriceMismatch";
    ExchangeMetric[ExchangeMetric["PopZoneExcluded"] = 92] = "PopZoneExcluded";
    ExchangeMetric[ExchangeMetric["PopTestAuction"] = 93] = "PopTestAuction";
    ExchangeMetric[ExchangeMetric["PopAuctionType"] = 94] = "PopAuctionType";
    ExchangeMetric[ExchangeMetric["PopCurrency"] = 95] = "PopCurrency";
    ExchangeMetric[ExchangeMetric["PopLandingIgnored"] = 96] = "PopLandingIgnored";
})(ExchangeMetric || (ExchangeMetric = {}));
export const EXCHANGE_METRIC_LABELS = {
    [ExchangeMetric.SspRequests]: "SSP Requests",
    [ExchangeMetric.SspThrottled]: "Throttled SSP Requests",
    [ExchangeMetric.SspPaused]: "Paused SSP Requests",
    [ExchangeMetric.SspFiltered]: "Filtered SSP Requests",
    [ExchangeMetric.SspWins]: "SSP Wins",
    [ExchangeMetric.DspRequests]: "DSP Requests",
    [ExchangeMetric.DspSkippedQps]: "DSP Requests Skipped by QPS",
    [ExchangeMetric.DspSkippedFilter]: "DSP Requests Skipped by Filter",
    [ExchangeMetric.DspResponses]: "DSP Responses",
    [ExchangeMetric.DspNoBid]: "DSP No Bid",
    [ExchangeMetric.DspTimeouts]: "Timeouts",
    [ExchangeMetric.DspHttpErrors]: "HTTP Errors",
    [ExchangeMetric.InvalidBids]: "Invalid Bids",
    [ExchangeMetric.BidsBelowFloor]: "Bids Below Floor",
    [ExchangeMetric.DspWins]: "DSP Wins",
    [ExchangeMetric.BelowFloorAfterShare]: "Below Floor After Share",
    [ExchangeMetric.ValidBids]: "Valid Bids",
    [ExchangeMetric.SegmentMatched]: "Segment Matched Requests",
    [ExchangeMetric.RedisUnavailable]: "Redis Unavailable",
    [ExchangeMetric.PopRequests]: "Pop Requests",
    [ExchangeMetric.PopDisabled]: "Pop Disabled",
    [ExchangeMetric.PopNoMatch]: "Pop No Matching Campaign",
    [ExchangeMetric.PopExhausted]: "Pop Campaign Exhausted",
    [ExchangeMetric.PopBelowFloor]: "Pop Below Floor",
    [ExchangeMetric.PopIvt]: "Pop Invalid Traffic (bid)",
    [ExchangeMetric.PopNoRedis]: "Pop Redis Unavailable",
    [ExchangeMetric.PopDeadline]: "Pop Deadline Missed",
    [ExchangeMetric.PopCappedAtBid]: "Pop Capped at Bid",
    [ExchangeMetric.PopError]: "Pop Errors",
    [ExchangeMetric.PopBids]: "Pop Bids",
    [ExchangeMetric.PopWins]: "Pop Wins",
    [ExchangeMetric.PopBilled]: "Pop Billed",
    [ExchangeMetric.PopNoticeInvalid]: "Pop Invalid Notices",
    [ExchangeMetric.PopVisits]: "Pop Visits",
    [ExchangeMetric.PopCapUnchecked]: "Pop Visits Cap Unchecked",
    [ExchangeMetric.PopCappedAtVisit]: "Pop Capped at Visit",
    [ExchangeMetric.PopInvalidToken]: "Pop Invalid Tokens",
    [ExchangeMetric.PopReplay]: "Pop Replays",
    [ExchangeMetric.PopVisitPersistFailed]: "Pop Visit Persist Failed",
    [ExchangeMetric.PopConversions]: "Pop Conversions",
    [ExchangeMetric.PopPostbackRejected]: "Pop Postbacks Rejected",
    [ExchangeMetric.PopTokenTooLarge]: "Pop Token Too Large",
    [ExchangeMetric.PopVisitUncertain]: "Pop Visit Uncertain",
    [ExchangeMetric.PopReservationReleased]: "Pop Reservations Released",
    [ExchangeMetric.PopLatGt25]: "Pop Bid Latency > 25 ms",
    [ExchangeMetric.PopHead]: "Pop HEAD Requests",
    [ExchangeMetric.PopPrefetch]: "Pop Prefetches",
    [ExchangeMetric.PopExpired]: "Pop Expired Tokens",
    [ExchangeMetric.PopExpiredStale]: "Pop Stale Tokens",
    [ExchangeMetric.PopCappedReplay]: "Pop Capped Replays",
    [ExchangeMetric.PopWrongPurpose]: "Pop Wrong-Purpose Tokens",
    [ExchangeMetric.PopNoticeIpDenied]: "Pop Notices IP Denied",
    [ExchangeMetric.PopNoticePersistFailed]: "Pop Notice Persist Failed",
    [ExchangeMetric.PopNoticeUncertain]: "Pop Notice Uncertain",
    [ExchangeMetric.PopVisitIvt]: "Pop Invalid Visits",
    [ExchangeMetric.PopFpMismatch]: "Pop Fingerprint Mismatches",
    [ExchangeMetric.PopConfirmed]: "Pop Confirmed Visits",
    [ExchangeMetric.PopPostbackStatus]: "Pop Postbacks Unknown Status",
    [ExchangeMetric.PopRegionUnknown]: "Pop Region Unknown",
    [ExchangeMetric.PopPriceMismatch]: "Pop Price Mismatches",
    [ExchangeMetric.PopZoneExcluded]: "Pop Zone Excluded",
    [ExchangeMetric.PopTestAuction]: "Pop Test Auctions",
    [ExchangeMetric.PopAuctionType]: "Pop Unsupported Auction Type",
    [ExchangeMetric.PopCurrency]: "Pop Unsupported Currency",
    [ExchangeMetric.PopLandingIgnored]: "Pop Landing Postbacks Ignored",
};
// Outcome of one DSP call (dspCall.outcome).
export var DspCallOutcome;
(function (DspCallOutcome) {
    DspCallOutcome[DspCallOutcome["Response"] = 1] = "Response";
    DspCallOutcome[DspCallOutcome["NoBid"] = 2] = "NoBid";
    DspCallOutcome[DspCallOutcome["Timeout"] = 3] = "Timeout";
    DspCallOutcome[DspCallOutcome["HttpError"] = 4] = "HttpError";
    DspCallOutcome[DspCallOutcome["ParseError"] = 5] = "ParseError";
    DspCallOutcome[DspCallOutcome["FetchError"] = 6] = "FetchError";
    DspCallOutcome[DspCallOutcome["SkippedQps"] = 7] = "SkippedQps";
    DspCallOutcome[DspCallOutcome["SkippedFilter"] = 8] = "SkippedFilter";
    DspCallOutcome[DspCallOutcome["SkippedUnsafe"] = 9] = "SkippedUnsafe";
    DspCallOutcome[DspCallOutcome["SkippedFanout"] = 10] = "SkippedFanout";
})(DspCallOutcome || (DspCallOutcome = {}));
// --- Exact money arithmetic -------------------------------------------------
//
// Money is carried as INTEGER MICRO-DOLLARS (1 USD = 1_000_000 micros) and
// percentages as BASIS POINTS (100% = 10_000 bp). JS numbers are exact for
// integers below 2^53 (~9e15 micros = ~9e9 USD), so plain integer arithmetic is
// exact; every function rounds to an integer so no fraction ever leaks.
// CPM figures are micro-dollars PER THOUSAND impressions.
export const MICROS_PER_DOLLAR = 1_000_000;
export const BP_PER_WHOLE = 10_000;
export const toMicros = (dollars) => Number.isFinite(dollars) ? Math.round(dollars * MICROS_PER_DOLLAR) : 0;
export const fromMicros = (micros) => Number(micros) / MICROS_PER_DOLLAR;
export const pctToBp = (pct) => Number.isFinite(pct) ? Math.round(pct * 100) : 0;
export const bpToPct = (bp) => bp / 100;
// share of an amount, floored to a whole micro (never over-pays by rounding up).
export const applyShareMicros = (micros, bp) => Math.floor((micros * bp) / BP_PER_WHOLE);
// A CPM (micros per 1000) over N impressions, in micros.
export const cpmMicrosToSpend = (cpmMicros, impressions) => Math.floor((cpmMicros * impressions) / 1000);
// --- Reporting formulas (TZ §5.5 / §9.5) ------------------------------------
//
// All ratio metrics are computed from TOTALS — never averaged across rows — so
// a total row recomputes each ratio from the summed absolutes. Inputs are
// plain numbers (counts, or micros for money); outputs are percentages (0..100)
// or micros for eCPM. Division by zero yields null ("n/a"), never NaN/Infinity.
const ratio = (num, den) => den > 0 ? num / den : null;
export const formulas = {
    // micros per 1000 impressions
    ecpmMicros: (spendMicros, impressions) => {
        const r = ratio(spendMicros * 1000, impressions);
        return r === null ? null : Math.round(r);
    },
    ctrPct: (clicks, impressions) => {
        const r = ratio(clicks, impressions);
        return r === null ? null : r * 100;
    },
    vcrPct: (completes, impressions) => {
        const r = ratio(completes, impressions);
        return r === null ? null : r * 100;
    },
    profitMicros: (dspRevenueMicros, sspSpendMicros) => dspRevenueMicros - sspSpendMicros,
    marginPct: (dspRevenueMicros, sspSpendMicros) => {
        const r = ratio(dspRevenueMicros - sspSpendMicros, dspRevenueMicros);
        return r === null ? null : r * 100;
    },
    fillRatePct: (impressions, sspRequests) => {
        const r = ratio(impressions, sspRequests);
        return r === null ? null : r * 100;
    },
    renderRatePct: (impressions, wins) => {
        const r = ratio(impressions, wins);
        return r === null ? null : r * 100;
    },
    timeoutRatePct: (timeouts, dspRequests) => {
        const r = ratio(timeouts, dspRequests);
        return r === null ? null : r * 100;
    },
    // Win rate denominator = valid bids (TZ §5.5 recommendation), shared by UI + API.
    winRatePct: (dspWins, validBids) => {
        const r = ratio(dspWins, validBids);
        return r === null ? null : r * 100;
    },
    responseRatePct: (responses, dspRequests) => {
        const r = ratio(responses, dspRequests);
        return r === null ? null : r * 100;
    },
    // Popunder ratios, all from totals.
    popWinRatePct: (popWins, popBids) => {
        const r = ratio(popWins, popBids);
        return r === null ? null : r * 100;
    },
    popVisitRatePct: (popVisits, popBilled) => {
        const r = ratio(popVisits, popBilled);
        return r === null ? null : r * 100;
    },
    popConfirmRatePct: (popConfirmed, popVisits) => {
        const r = ratio(popConfirmed, popVisits);
        return r === null ? null : r * 100;
    },
};
//# sourceMappingURL=exchange.js.map